import { useState, FormEvent } from 'react';
import { ChevronDown } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  inquiryType: string;
  message: string;
}

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

const INQUIRY_TYPES = ['Solutions', 'PhonoLex', 'Edutainment', 'Other'];

export default function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    inquiryType: '',
    message: '',
  });
  const [status, setStatus] = useState<SubmitStatus>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const body = new FormData(e.currentTarget);
      body.append('access_key', '6cea498c-31e0-49f4-a138-8f5042537084');
      body.append('subject', `New message from ${formData.name}`);

      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body,
      });
      const result = await res.json();

      if (result.success) {
        setStatus('success');
        setFormData({ name: '', email: '', inquiryType: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <p className="font-sans text-sm text-vellum-muted animate-fade-in">
        Message sent. I'll be in touch.
      </p>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-md">
      {[
        { name: 'name', label: 'name', type: 'text', required: true },
        { name: 'email', label: 'email', type: 'email', required: true },
      ].map((field) => (
        <div key={field.name}>
          <label htmlFor={field.name} className="block font-sans text-xs tracking-wide text-vellum-muted mb-1">
            {field.label}
          </label>
          <input
            type={field.type}
            id={field.name}
            name={field.name}
            value={formData[field.name as keyof FormData]}
            onChange={handleChange}
            required={field.required}
            className="w-full bg-transparent border-0 border-b border-rule py-2 text-vellum font-serif text-sm focus:border-accent focus:outline-none transition-colors"
          />
        </div>
      ))}

      <div>
        <label htmlFor="inquiryType" className="block font-sans text-xs tracking-wide text-vellum-muted mb-1">
          inquiry type
        </label>
        <div className="relative">
          <select
            id="inquiryType"
            name="inquiryType"
            value={formData.inquiryType}
            onChange={handleChange}
            required
            className="w-full bg-transparent border-0 border-b border-rule py-2 pr-7 text-vellum font-serif text-sm focus:border-accent focus:outline-none transition-colors appearance-none"
          >
            <option value="" disabled>select one</option>
            {INQUIRY_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
          <ChevronDown
            className="absolute right-1 top-1/2 -translate-y-1/2 w-4 h-4 text-vellum-muted pointer-events-none"
            aria-hidden="true"
          />
        </div>
      </div>

      <div>
        <label htmlFor="message" className="block font-sans text-xs tracking-wide text-vellum-muted mb-1">
          message
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          rows={4}
          className="w-full bg-transparent border-0 border-b border-rule py-2 text-vellum font-serif text-sm focus:border-accent focus:outline-none transition-colors resize-none"
        />
      </div>

      <div className="flex items-center justify-end gap-4">
        {status === 'error' && (
          <span className="font-sans text-xs text-vellum-muted">
            Something went wrong. Try again.
          </span>
        )}
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="font-sans text-sm tracking-wide border border-accent text-accent px-5 py-2 hover:bg-accent hover:text-ink transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {status === 'submitting' ? 'sending...' : 'send'}
        </button>
      </div>
    </form>
  );
}
